import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  Account,
  Contact,
  Conversation,
  DisappearTimerSec,
  GroupRecord,
  SecurityEvent,
} from "@veto/domain";
import { sortConversations, type MessageEvent, type TransportChannel } from "@veto/domain";
import { getAccount, listContacts, listConversations, listSecurityEvents } from "@veto/storage";
import { RelayPool, type DirectPeerLink as PeerLink } from "@veto/transport";
import { updateMessageDelivery, upsertConversation } from "@veto/storage";
import { type SessionBundle } from "./messaging/accountLifecycle";
import { createMessenger } from "./messaging/messenger";
import { recoverOrphanedOutbound } from "./messaging/outboxRecovery";
import {
  captureInviteFromLocation,
  hasPendingInvite,
  listenForLaunchInvites,
} from "./messaging/pendingInvite";
import { directConversationId } from "./messaging/wire";
import { applyDefaultDisappear } from "./chatPrefs";
import { applyNetPrefs, directLinkAllowed, loadNetPrefs, NET_PREFS_EVENT } from "./netPrefs";
import { localSinceStore } from "./messaging/fallbackTransport";
import { getMeta } from "@veto/storage";
import { forgetMediaBlobs } from "./messaging/mediaBlobs";
import { useEncryptedMessages } from "./hooks/useEncryptedMessages";
import { createChatActions } from "./hooks/useChatActions";
import { createGroupActions } from "./hooks/useGroupActions";
import { createAccountHandlers } from "./hooks/useAccountHandlers";
import { createProfileActions } from "./hooks/useProfileActions";
import { createConversationChrome } from "./hooks/useConversationChrome";
import { createPollActions } from "./hooks/usePollActions";
import { withPollAndLockProps } from "./buildShellProps";
import { useChatLockSession } from "./hooks/useChatLockSession";
import { useRelayInbox } from "./hooks/useRelayInbox";
import {
  useDisappearSweep,
  useDraftAutosave,
  useNotificationDeepLinks,
  useSessionRotateOnRevoke,
  useWireOutbox,
} from "./hooks/useAppSideEffects";
import { useAppBootstrap } from "./hooks/useAppBootstrap";
import { useAtRestVaultCipher } from "./hooks/useAtRestVaultCipher";
import { useEphemeralToasts } from "./hooks/useEphemeralToasts";
import { useOpenConversation } from "./hooks/useOpenConversation";
import { useVaultLockControls } from "./hooks/useVaultLockControls";
import { type SettingsTab } from "./screens/SettingsPanel";
import { type SecureVaultCapabilities } from "./secureVault";
import { BoundAppShell } from "./components/bindAppShell";
import { AppGate } from "./components/AppGate";
import {
  getNotificationPermission,
  loadNotificationPrefs,
  requestNotificationPermission,
  saveNotificationPrefs,
  syncAppBadge,
  type InAppBannerPayload,
  type NotificationPrefs,
  type NotificationPermissionState,
} from "./notifications";
import type { MessageHit } from "./components/ConversationListPane";

type Screen = "loading" | "onboarding" | "lock" | "app" | "error";
export function App() {
  const [screen, setScreen] = useState<Screen>("loading");
  const [account, setAccount] = useState<Account | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [messages, setMessages] = useState<MessageEvent[]>([]);
  const [draft, setDraft] = useState("");
  const [session, setSession] = useState<SessionBundle | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settingsTab, setSettingsTab] = useState<SettingsTab>("privacy");
  const [toast, setToast] = useState<string | null>(null);
  const [relayStatus, setRelayStatus] = useState("offline");
  const [vaultKeyB64, setVaultKeyB64] = useState<string | null>(null);
  const [securityEvents, setSecurityEvents] = useState<SecurityEvent[]>([]);
  const [query, setQuery] = useState("");
  const [banner, setBanner] = useState<InAppBannerPayload | null>(null);
  const [replyTo, setReplyTo] = useState<MessageEvent | null>(null);
  const [editing, setEditing] = useState<MessageEvent | null>(null);
  const [chatQuery, setChatQuery] = useState("");
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  /** Conversation id awaiting a chat-lock PIN (replaces window.prompt — native-shell safe). */
  const [chatLockPromptFor, setChatLockPromptFor] = useState<string | null>(null);
  const [activeGroup, setActiveGroup] = useState<GroupRecord | null>(null);
  const [vaultCaps, setVaultCaps] = useState<SecureVaultCapabilities | null>(null);
  const [notificationPrefs, setNotificationPrefs] = useState<NotificationPrefs>(() =>
    loadNotificationPrefs(),
  );
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermissionState>(
    () => getNotificationPermission(),
  );
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const activeIdRef = useRef<string | null>(null);
  const contactsRef = useRef<Contact[]>([]);
  const relay = useMemo(() => new RelayPool({ sinceStore: localSinceStore }), []);
  useEffect(() => () => relay.close(), [relay]);
  const [netPrefsVersion, setNetPrefsVersion] = useState(0);
  useEffect(() => {
    const bump = () => setNetPrefsVersion((v) => v + 1);
    window.addEventListener(NET_PREFS_EVENT, bump);
    return () => window.removeEventListener(NET_PREFS_EVENT, bump);
  }, []);
  const peersRef = useRef(new Map<string, PeerLink>());
  const voiceUrlsRef = useRef(new Map<string, string>());
  const [, bumpVoice] = useState(0);
  const seenEventIds = useMemo(() => new Set<string>(), []);
  const unverifiedCount = contacts.filter((c) => c.verificationState !== "verified").length;
  const openSecurityCount = securityEvents.filter((e) => !e.acknowledged).length;

  useEffect(() => {
    activeIdRef.current = activeId;
    contactsRef.current = contacts;
  }, [activeId, contacts]);

  // Layered transport plan (custom relays, auto-adopted relays, public mesh).
  useEffect(() => {
    applyNetPrefs();
  }, []);

  const [disappearTimer, setDisappearTimer] = useState<DisappearTimerSec>(0);
  const [groupForkWarning, setGroupForkWarning] = useState<string | null>(null);

  const filteredConversations = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = sortConversations(conversations);
    if (!q) return base;
    return base.filter(
      (c) => c.title.toLowerCase().includes(q) || c.lastMessagePreview.toLowerCase().includes(q),
    );
  }, [conversations, query]);

  // Scrolling is ChatPane's job now: it lands on the first unread message, and only follows new
  // messages while you're at the bottom (no yanking while you read history).
  const [unreadAtOpen, setUnreadAtOpen] = useState(0);

  const { putMessage, insertMessage, listMessages } = useEncryptedMessages(vaultKeyB64);

  /** Starred messages of one chat (or all chats), newest first; locked chats only when asked for. */
  const loadStarred = useCallback(
    async (conversationId: string | null): Promise<MessageHit[]> => {
      const out: MessageHit[] = [];
      for (const c of conversations) {
        if (conversationId ? c.id !== conversationId : !!(await getMeta(`chatlock.${c.id}`)))
          continue;
        for (const m of await listMessages(c.id)) {
          if (m.starred && !m.deletedForEveryone && !m.deletedForMe) {
            out.push({
              conversationId: c.id,
              title: c.title,
              eventId: m.eventId,
              body: m.body,
              createdAt: m.createdAt,
            });
          }
        }
      }
      return out.sort((a, b) => b.createdAt - a.createdAt);
    },
    [conversations, listMessages],
  );

  /** Search every chat's decrypted messages on this device (locked chats never). Phone parity. */
  const searchMessages = useCallback(
    async (q: string): Promise<MessageHit[]> => {
      const needle = q.trim().toLowerCase();
      if (needle.length < 2) return [];
      const hits: MessageHit[] = [];
      for (const c of conversations) {
        if (await getMeta(`chatlock.${c.id}`)) continue;
        for (const m of await listMessages(c.id)) {
          if (m.deletedForEveryone || m.deletedForMe || !m.body.toLowerCase().includes(needle))
            continue;
          hits.push({
            conversationId: c.id,
            title: c.title,
            eventId: m.eventId,
            body: m.body,
            createdAt: m.createdAt,
          });
        }
      }
      return hits.sort((a, b) => b.createdAt - a.createdAt).slice(0, 50);
    },
    [conversations, listMessages],
  );

  const activeConv = conversations.find((c) => c.id === activeId) ?? null;
  const activeContact = contacts.find((c) => c.id === activeConv?.contactId) ?? null;

  useAtRestVaultCipher(vaultKeyB64);

  const refresh = useCallback(async () => {
    setAccount((await getAccount()) ?? null);
    setContacts(await listContacts());
    const convs = await listConversations();
    setConversations(convs);
    setSecurityEvents(await listSecurityEvents());
    void syncAppBadge(convs.reduce((n, c) => n + (c.unread || 0), 0));
  }, []);

  useAppBootstrap({ setScreen, setAccount, setToast, setVaultCaps });

  const chatLock = useChatLockSession();
  const [showPollModal, setShowPollModal] = useState(false);
  const { openConversation } = useOpenConversation({
    chatLock,
    listMessages,
    setActiveId,
    setDraft,
    setReplyTo,
    setEditing,
    setChatQuery,
    setShowDetails,
    setMessages,
    setActiveGroup,
    setGroupForkWarning,
    setDisappearTimer,
    setUnreadAtOpen,
    refresh,
  });

  useNotificationDeepLinks({ screen, openConversation });
  useEphemeralToasts({
    banner,
    toast,
    clearBanner: () => setBanner(null),
    clearToast: () => setToast(null),
  });

  // FR-CON-001: invite links / share target / launch queue open "New chat" with
  // the invite pre-verified, so adding someone is one tap after unlock.
  useEffect(() => {
    if (screen !== "app") return;
    const url = new URL(window.location.href);
    const flag = url.searchParams.get("addContact") ?? url.searchParams.get("add");
    captureInviteFromLocation();
    if (hasPendingInvite() || flag === "1" || flag === "true") setShowAdd(true);
    if (flag) {
      url.searchParams.delete("addContact");
      url.searchParams.delete("add");
      window.history.replaceState(
        null,
        "",
        `${url.pathname}${url.searchParams.toString() ? `?${url.searchParams}` : ""}`,
      );
    }
    listenForLaunchInvites(() => setShowAdd(true));
  }, [screen]);

  // Protocol v2: every send goes through one authenticated pipeline.
  const messenger = useMemo(
    () =>
      session && account
        ? createMessenger({ store: session.store, account, relay, peers: peersRef.current })
        : null,
    [session, account, relay],
  );
  const inboundRef = useRef<((ev: unknown, hint?: TransportChannel) => void) | null>(null);
  const onPeerFrame = useCallback((data: string) => {
    try {
      inboundRef.current?.(["EVENT", "direct", JSON.parse(data)], "direct");
    } catch {
      /* malformed direct frame */
    }
  }, []);

  useRelayInbox({
    screen,
    account,
    session,
    messenger,
    inboundRef,
    netPrefsVersion,
    relay,
    seenEventIds,
    activeIdRef,
    contactsRef,
    peersRef,
    voiceUrlsRef,
    putMessage,
    insertMessage,
    listMessages,
    setRelayStatus,
    setToast,
    setBanner,
    setMessages,
    setActiveGroup,
    bumpVoice: () => bumpVoice((n) => n + 1),
    refresh,
  });

  useDraftAutosave({ screen, activeId, draft, replyTo });
  useSessionRotateOnRevoke({ screen, session, showSettings });
  useWireOutbox({
    screen,
    account,
    relay,
    peers: peersRef.current,
    // SPEC §12.2: retry outcomes flow back into the open chat's bubbles.
    onMessageStatus: (u) =>
      setMessages((prev) =>
        prev.some((x) => x.eventId === u.messageEventId)
          ? prev.map((x) =>
              x.eventId === u.messageEventId
                ? { ...x, status: u.status, transport: u.transport ?? x.transport }
                : x,
            )
          : prev,
      ),
  });

  // One-shot after unlock: re-send queued rows that never reached the wire outbox
  // (protocol-v1 leftovers, or a crash between local commit and sealing).
  const recoveredRef = useRef(false);
  useEffect(() => {
    if (screen !== "app" || !messenger || recoveredRef.current || contacts.length === 0) return;
    recoveredRef.current = true;
    void recoverOrphanedOutbound({
      messenger,
      contacts,
      conversations,
      listMessages,
      onStatus: async (eventId, status, transport) => {
        await updateMessageDelivery(eventId, { status, transport });
        setMessages((prev) =>
          prev.map((x) => (x.eventId === eventId ? { ...x, status, transport } : x)),
        );
      },
    });
  }, [screen, messenger, contacts, conversations, listMessages]);

  const { completeOnboarding, unlock, addContact } = createAccountHandlers({
    session,
    account,
    setVaultKeyB64,
    setAccount,
    setSession,
    setScreen,
    setToast,
    setShowAdd,
    setSettingsTab,
    setShowSettings,
    refresh,
    openConversation,
  });

  // FR-MSG-004: durable sweep refreshes list previews after deleting rows.
  useDisappearSweep({ activeId, setMessages, onPurged: () => void refresh() });

  const { lockVault } = useVaultLockControls({
    screen,
    session,
    setVaultKeyB64,
    setSession,
    setMessages,
    setActiveId,
    setDraft,
    setReplyTo,
    setEditing,
    setShowDetails,
    setShowSettings,
    setShowAdd,
    setBanner,
    setScreen,
    // SEC: decrypted voice-note blob URLs must not survive lock.
    onLocked: () => {
      for (const url of voiceUrlsRef.current.values()) {
        try {
          URL.revokeObjectURL(url);
        } catch {
          /* ignore */
        }
      }
      voiceUrlsRef.current.clear();
      forgetMediaBlobs(); // decrypted bytes kept for forwarding go with the URLs
      bumpVoice((n) => n + 1);
    },
  });

  const chat = createChatActions({
    messenger,
    activeId,
    activeConv,
    activeContact,
    activeGroup,
    contacts,
    draft,
    replyTo,
    editing,
    disappearTimer,
    peers: peersRef.current,
    voiceUrls: voiceUrlsRef.current,
    bumpVoice: () => bumpVoice((n) => n + 1),
    onPeerFrame,
    putMessage,
    listMessages,
    setDraft,
    setReplyTo,
    setEditing,
    setMessages,
    setToast,
    refresh,
  });

  const groups = createGroupActions({
    messenger,
    account,
    contacts,
    activeGroup,
    putMessage,
    listMessages,
    setActiveGroup,
    setShowCreateGroup,
    setMessages,
    setToast,
    refresh,
    openConversation,
  });

  // SPEC §11: an open direct link only lives while it's allowed — "Direct connections" on, the
  // contact verified and not blocked. Turning the setting off, a key change or a block closes it.
  useEffect(() => {
    const on = loadNetPrefs().peerMesh;
    for (const [convId, link] of peersRef.current) {
      const conv = conversations.find((c) => c.id === convId);
      const contact = contacts.find((c) => c.id === conv?.contactId);
      if (!contact || !directLinkAllowed(contact, on)) {
        link.close();
        peersRef.current.delete(convId);
      }
    }
  }, [netPrefsVersion, contacts, conversations]);

  // Peer mesh auto-establish (SPEC §11): opportunistic direct WebRTC link with the
  // (accepted) contact of the open conversation. Frames re-enter the inbound pipeline.
  const autoPeerAtRef = useRef(new Map<string, number>());
  const chatRef = useRef(chat);
  chatRef.current = chat;
  useEffect(() => {
    if (screen !== "app" || !messenger) return;
    if (!activeId || !activeContact || activeContact.requestState === "pending") return;
    // SPEC §11: automatic direct links only with VERIFIED contacts (IP exposure).
    if (!directLinkAllowed(activeContact)) return;
    const state = peersRef.current.get(activeId)?.state;
    if (state === "open" || state === "connecting") return;
    const last = autoPeerAtRef.current.get(activeId) ?? 0;
    if (Date.now() - last < 60_000) return;
    autoPeerAtRef.current.set(activeId, Date.now());
    // An offer can cross the other side's verification (it dropped ours; ours ignored theirs as
    // glare): retry up to twice after a random pause, while this chat stays open.
    const convId = activeId;
    const attempt = (n: number) =>
      void chatRef.current.tryOpenDirectPeer({
        quiet: true,
        onFailed: () => {
          if (n >= 2) return;
          window.setTimeout(() => {
            if (activeIdRef.current !== convId) return;
            const st = peersRef.current.get(convId)?.state;
            if (st === "open" || st === "connecting") return;
            attempt(n + 1);
          }, 2000 + Math.random() * 4000);
        },
      });
    attempt(0);
  }, [
    screen,
    messenger,
    activeId,
    activeContact?.id,
    activeContact?.requestState,
    activeContact?.verificationState,
    activeContact?.blockedAt,
    netPrefsVersion,
  ]);

  const chrome = createConversationChrome({
    activeId,
    activeConv,
    activeContact,
    chatLock: {
      has: chatLock.hasLock,
      enable: chatLock.enableLock,
      disable: chatLock.disableLock,
    },
    setDisappearTimer,
    setToast,
    setActiveId,
    setMessages,
    requestChatLockEnable: (conversationId) => setChatLockPromptFor(conversationId),
    refresh,
    // The encryption session is kept (review H2/M1): only content is deleted.
    onConversationDeleted: (conversationId) => {
      peersRef.current.get(conversationId)?.close();
      peersRef.current.delete(conversationId);
    },
  });

  /** New chat → existing contact: open (or recreate) the direct conversation. */
  async function openContactChat(contactId: string) {
    const c = contacts.find((x) => x.id === contactId);
    if (!c || !account) return;
    const convId = await directConversationId(
      account.messagingPublicKeyHex,
      c.messagingPublicKeyHex,
    );
    if (!conversations.some((x) => x.id === convId)) {
      await upsertConversation({
        id: convId,
        type: "direct",
        title: c.displayName,
        contactId: c.id,
        lastMessagePreview: "",
        lastMessageAt: Date.now(),
        unread: 0,
        pinned: false,
        muted: false,
      });
      await applyDefaultDisappear(convId);
    }
    setShowAdd(false);
    await refresh();
    await openConversation(convId);
  }

  /** Settings → Storage & data: drop the decrypted media kept in memory (blob URLs). */
  function clearMediaCache(): number {
    const n = voiceUrlsRef.current.size;
    for (const url of voiceUrlsRef.current.values()) {
      try {
        URL.revokeObjectURL(url);
      } catch {
        /* ignore */
      }
    }
    voiceUrlsRef.current.clear();
    forgetMediaBlobs(); // and the decrypted bytes kept next to them for forwarding
    bumpVoice((v) => v + 1);
    return n;
  }

  const profile = createProfileActions({
    messenger,
    account,
    contacts,
    setAccount,
    setToast,
    refresh,
  });

  const polls = createPollActions({
    messenger,
    activeId,
    activeConv,
    activeContact,
    activeGroup,
    contacts,
    putMessage,
    setMessages,
    setToast,
    refresh,
  });

  const shell = withPollAndLockProps(
    {
      account,
      sessionIdentity: session?.identity ?? null,
      relayStatus,
      relayClient: relay,
      query,
      setQuery,
      conversations,
      filteredConversations,
      contacts,
      activeId,
      activeConv,
      activeContact,
      activeGroup,
      messages,
      unreadAtOpen,
      searchMessages,
      loadStarred,
      chatQuery,
      setChatQuery,
      draft,
      setDraft,
      replyTo,
      editing,
      setReplyTo,
      setEditing,
      messagesEndRef,
      voiceUrlFor: (id: string) => voiceUrlsRef.current.get(id),
      showDetails,
      setShowDetails,
      showAdd,
      setShowAdd,
      showCreateGroup,
      setShowCreateGroup,
      showSettings,
      setShowSettings,
      settingsTab,
      setSettingsTab,
      unverifiedCount,
      openSecurityCount,
      securityEvents,
      vaultCaps,
      notificationPrefs,
      setNotificationPrefs,
      saveNotificationPrefs,
      notificationPermission,
      setNotificationPermission,
      requestNotificationPermission,
      banner,
      setBanner,
      toast,
      setToast,
      openConversation,
      addContact,
      chat,
      groups,
      refresh,
      setActiveId,
      onLockNow: lockVault,
      onSaveProfile: profile.saveProfile,
      ...chrome,
      mediaCacheCount: voiceUrlsRef.current.size,
      onClearMediaCache: clearMediaCache,
      onOpenContact: (contactId: string) => void openContactChat(contactId),
    },
    {
      showPollModal,
      setShowPollModal,
      createAndSendPoll: polls.createAndSendPoll,
      voteOnPoll: polls.voteOnPoll,
      pendingChatLock: chatLock.pendingLock,
      markUnlocked: chatLock.markUnlocked,
      setPendingLock: chatLock.setPendingLock,
      enableLock: chatLock.enableLock,
      chatLockPromptFor,
      setChatLockPromptFor,
      openConversation,
      setActiveId,
      setToast,
      activeId,
      disappearTimer,
      groupForkWarning,
    },
  );

  return (
    <AppGate screen={screen} onCompleteOnboarding={completeOnboarding} onUnlock={unlock}>
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      <BoundAppShell {...(shell as any)} />
    </AppGate>
  );
}
